import React, { useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TablePagination, Select, MenuItem,
  IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Button, CircularProgress,
  useMediaQuery
} from "@mui/material";
import { CheckCircle, Cancel, AccessTime, Visibility, Replay, Block } from "@mui/icons-material";
import { toast } from "react-toastify";
import baseURL from "../../config";

const MS_COLORS = {
  primary: '#2D4A3D',
  primaryLight: '#EBF5EE',
  text: '#323130',
  textSecondary: '#605D5C',
  background: '#FFFFFF',
  backgroundLight: '#F3F2F1',
  border: '#E1E1E1',
  hoverBackground: '#F0F0F0',
  white: '#FFFFFF',
  accent: '#107C10',
  red: '#D83B01',
  orange: '#FF8C00',
  blue: '#2D4A3D',
};

const headerCellSx = { fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text };
const bodyCellSx = { fontSize: "0.8rem", color: MS_COLORS.textSecondary, whiteSpace: "nowrap" };

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.5 }}>
    <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, fontWeight: 500, minWidth: 140 }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: MS_COLORS.text, textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>{value}</Typography>
  </Box>
);

const TransactionsManagement = ({ transactions }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState("all");

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Retry dialog state
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryTransaction, setRetryTransaction] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  // Mark-failed dialog state
  const [markFailedOpen, setMarkFailedOpen] = useState(false);
  const [markFailedTransaction, setMarkFailedTransaction] = useState(null);
  const [markFailedLoading, setMarkFailedLoading] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event) => setStatusFilter(event.target.value);

  const filteredTransactions =
    statusFilter === "all"
      ? transactions
      : transactions.filter((transaction) => transaction.status === statusFilter);

  // Detail dialog handlers
  const handleViewDetail = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailOpen(true);
  };

  // Retry handlers
  const handleRetry = (transaction) => {
    setRetryTransaction(transaction);
    setRetryOpen(true);
  };

  const handleRetryConfirm = async () => {
    setRetryLoading(true);
    try {
      const response = await fetch(`${baseURL}/admin/admin-transactions/${retryTransaction.id}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setRetryOpen(false);
      } else {
        toast.error(data.message || 'Retry failed');
      }
    } catch (err) {
      console.error('Retry error:', err);
      toast.error('An error occurred while retrying the transaction');
    } finally {
      setRetryLoading(false);
    }
  };

  // Mark-failed handlers
  const handleMarkFailed = (transaction) => {
    setMarkFailedTransaction(transaction);
    setMarkFailedOpen(true);
  };

  const handleMarkFailedConfirm = async () => {
    setMarkFailedLoading(true);
    try {
      const response = await fetch(`${baseURL}/admin/admin-transactions/${markFailedTransaction.id}/mark-failed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setMarkFailedOpen(false);
        // Update the local transaction status so UI reflects immediately
        markFailedTransaction.status = 'Failed';
      } else {
        toast.error(data.message || 'Failed to mark transaction');
      }
    } catch (err) {
      console.error('Mark-failed error:', err);
      toast.error('An error occurred while marking the transaction as failed');
    } finally {
      setMarkFailedLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        border: "1px solid rgba(0,0,0,0.05)",
        p: 3,
        mt: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#1a1a2e",
          mb: 3,
          fontWeight: 800,
          fontSize: isSmallScreen ? "1.05rem" : "1.2rem",
          letterSpacing: "-0.3px",
        }}
      >
        Transactions Management
      </Typography>

      {/* Filter Dropdown */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
        <Select
          value={statusFilter}
          onChange={handleFilterChange}
          size="small"
          sx={{
            minWidth: 150,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.border },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.primary },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: MS_COLORS.primary },
            "& .MuiSelect-select": { color: MS_COLORS.text, fontSize: '0.875rem' },
          }}
        >
          <MenuItem value="all">All Transactions</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Failed">Failed</MenuItem>
          <MenuItem value="Processed">Processed</MenuItem>
        </Select>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 1,
          boxShadow: 0,
          overflowX: "auto",
          border: `1px solid ${MS_COLORS.border}`,
        }}
      >
        <Table sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
            <TableRow>
              <TableCell sx={headerCellSx}>Campaign Owner</TableCell>
              <TableCell sx={headerCellSx}>Type</TableCell>
              <TableCell sx={headerCellSx}>Amount (UGX)</TableCell>
              <TableCell sx={headerCellSx}>Fundraise</TableCell>
              <TableCell sx={headerCellSx}>Donor Name</TableCell>
              <TableCell sx={headerCellSx}>Donor Email</TableCell>
              <TableCell sx={headerCellSx}>Payment Method</TableCell>
              <TableCell sx={headerCellSx}>Date</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((transaction) => (
                <TableRow
                  key={transaction.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: MS_COLORS.hoverBackground },
                  }}
                >
                  <TableCell sx={bodyCellSx}>{transaction.user}</TableCell>
                  <TableCell sx={bodyCellSx}>{transaction.type}</TableCell>
                  <TableCell sx={bodyCellSx}>UGX {transaction.amount?.toLocaleString()}</TableCell>
                  <TableCell sx={bodyCellSx}>{transaction.fundraise || '-'}</TableCell>
                  <TableCell sx={bodyCellSx}>{transaction.donor_name || '-'}</TableCell>
                  <TableCell sx={bodyCellSx}>{transaction.donor_email || '-'}</TableCell>
                  <TableCell sx={bodyCellSx}>{transaction.payment_method || '-'}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    {transaction.date ? new Date(transaction.date).toLocaleDateString("en-GB") : '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem", py: 1, fontWeight: "bold", whiteSpace: "nowrap" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color:
                          transaction.status === "Completed" ? MS_COLORS.accent
                            : transaction.status === "Pending" ? MS_COLORS.orange
                              : transaction.status === "Processed" ? MS_COLORS.blue
                                : MS_COLORS.red,
                      }}
                    >
                      {transaction.status === "Completed" ? (
                        <CheckCircle fontSize="small" />
                      ) : transaction.status === "Pending" ? (
                        <AccessTime fontSize="small" />
                      ) : (
                        <Cancel fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                        {transaction.status}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewDetail(transaction)}>
                        <Visibility fontSize="small" sx={{ color: MS_COLORS.primary }} />
                      </IconButton>
                    </Tooltip>
                    {transaction.status === "Failed" && transaction.type === "Donation Received" && (
                      <Tooltip title="Retry Payment">
                        <IconButton size="small" onClick={() => handleRetry(transaction)}>
                          <Replay fontSize="small" sx={{ color: MS_COLORS.orange }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {transaction.status === "Pending" && transaction.type === "Donation Received" && (
                      <Tooltip title="Mark as Failed">
                        <IconButton size="small" onClick={() => handleMarkFailed(transaction)}>
                          <Block fontSize="small" sx={{ color: MS_COLORS.red }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {filteredTransactions.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions":
            {
              fontSize: "0.8rem",
              color: MS_COLORS.textSecondary,
            },
          }}
        />
      )}

      {/* Transaction Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
          Transaction Details
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <DetailRow label="Transaction ID" value={`#${selectedTransaction.id}`} />
              <DetailRow label="Campaign Owner" value={selectedTransaction.user} />
              <DetailRow label="Type" value={selectedTransaction.type} />
              <DetailRow label="Amount" value={`UGX ${selectedTransaction.amount?.toLocaleString()}`} />
              <DetailRow label="Campaign" value={selectedTransaction.fundraise || 'N/A'} />
              <DetailRow label="Status" value={selectedTransaction.status} />
              <DetailRow label="Date" value={selectedTransaction.date ? new Date(selectedTransaction.date).toLocaleString() : 'N/A'} />
              <DetailRow label="Reference" value={selectedTransaction.external_reference || 'N/A'} />

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
                Donor Information
              </Typography>
              <DetailRow label="Donor Name" value={selectedTransaction.donor_name || 'N/A'} />
              <DetailRow label="Donor Email" value={selectedTransaction.donor_email || 'N/A'} />
              <DetailRow label="Donor Phone" value={selectedTransaction.donor_phone || 'N/A'} />
              <DetailRow label="Payment Method" value={selectedTransaction.payment_method || 'N/A'} />

              {selectedTransaction.rejection_reason && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <DetailRow label="Rejection Reason" value={selectedTransaction.rejection_reason} />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ pt: 2, px: 3 }}>
          <Button
            onClick={() => setDetailOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: "4px",
              fontSize: "0.85rem",
              textTransform: "none",
              borderColor: MS_COLORS.border,
              color: MS_COLORS.text,
              "&:hover": { borderColor: MS_COLORS.primary, backgroundColor: MS_COLORS.primaryLight + '30' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Confirmation Dialog */}
      <Dialog
        open={retryOpen}
        onClose={() => !retryLoading && setRetryOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
          Re-initiate Failed Transaction
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          {retryTransaction && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: MS_COLORS.textSecondary }}>
                {retryTransaction.payment_method === 'Mobile Money'
                  ? `This will send a new Mobile Money payment prompt to ${retryTransaction.donor_phone}. The donor will need to enter their PIN to complete the transaction.`
                  : `This will send an email to ${retryTransaction.donor_email || 'the donor'} with a link to retry their donation on the campaign page.`
                }
              </Typography>
              <Box sx={{ backgroundColor: MS_COLORS.backgroundLight, p: 2, borderRadius: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
                  Amount: UGX {retryTransaction.amount?.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Campaign: {retryTransaction.fundraise}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Donor: {retryTransaction.donor_name || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider sx={{ mt: 2 }} />
        <DialogActions sx={{ justifyContent: "flex-end", pt: 2, px: 3 }}>
          <Button
            onClick={() => setRetryOpen(false)}
            disabled={retryLoading}
            variant="outlined"
            sx={{
              borderRadius: "4px",
              fontSize: "0.85rem",
              textTransform: "none",
              borderColor: MS_COLORS.border,
              color: MS_COLORS.text,
              "&:hover": { borderColor: MS_COLORS.primary, backgroundColor: MS_COLORS.primaryLight + '30' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRetryConfirm}
            variant="contained"
            disabled={retryLoading}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              fontSize: "0.85rem",
              backgroundColor: MS_COLORS.primary,
              color: MS_COLORS.white,
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 120,
              "&:hover": { backgroundColor: MS_COLORS.primary, opacity: 0.9 },
              "&:disabled": { backgroundColor: MS_COLORS.border, color: MS_COLORS.textSecondary },
            }}
          >
            {retryLoading ? (
              <>
                <CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />
                Retrying...
              </>
            ) : (
              "Confirm Retry"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Failed Confirmation Dialog */}
      <Dialog
        open={markFailedOpen}
        onClose={() => !markFailedLoading && setMarkFailedOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px", p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem", color: MS_COLORS.text }}>
          Mark Transaction as Failed
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          {markFailedTransaction && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: MS_COLORS.textSecondary }}>
                This will mark the pending transaction as <strong>Failed</strong>
                {markFailedTransaction.donor_email
                  ? ` and send a failure notification email to ${markFailedTransaction.donor_email}.`
                  : '.'}
                {' '}The donor will then be able to retry the payment.
              </Typography>
              <Box sx={{ backgroundColor: MS_COLORS.backgroundLight, p: 2, borderRadius: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
                  Amount: UGX {markFailedTransaction.amount?.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Campaign: {markFailedTransaction.fundraise}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Donor: {markFailedTransaction.donor_name || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider sx={{ mt: 2 }} />
        <DialogActions sx={{ justifyContent: "flex-end", pt: 2, px: 3 }}>
          <Button
            onClick={() => setMarkFailedOpen(false)}
            disabled={markFailedLoading}
            variant="outlined"
            sx={{
              borderRadius: "4px",
              fontSize: "0.85rem",
              textTransform: "none",
              borderColor: MS_COLORS.border,
              color: MS_COLORS.text,
              "&:hover": { borderColor: MS_COLORS.primary, backgroundColor: MS_COLORS.primaryLight + '30' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMarkFailedConfirm}
            variant="contained"
            disabled={markFailedLoading}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              fontSize: "0.85rem",
              backgroundColor: MS_COLORS.red,
              color: MS_COLORS.white,
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 140,
              "&:hover": { backgroundColor: MS_COLORS.red, opacity: 0.9 },
              "&:disabled": { backgroundColor: MS_COLORS.border, color: MS_COLORS.textSecondary },
            }}
          >
            {markFailedLoading ? (
              <>
                <CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />
                Processing...
              </>
            ) : (
              "Mark as Failed"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionsManagement;
